import type { TutorialKapitelId } from './types';
import roheStolperfallen from './stolperfallen.md?raw';

/**
 * LERNZENTRUM — die kuratierten Stolperfallen aus `stolperfallen.md`.
 *
 * Warum eine Markdown-Datei und keine `.ts`-Konstante: die Liste wird von
 * Menschen aus echten Vorfaellen gepflegt (Plan §1.6, Auftrag A5) und von der
 * KI hoechstens umformuliert, nie ergaenzt. In Markdown liest und aendert sie
 * jeder; als TypeScript-Array waere jede Ergaenzung ein Code-Review.
 *
 * `?raw` backt den Text zur Bauzeit ins Bundle. Damit gibt es keinen
 * Netzabruf, keinen Ladezustand und keine Stelle, an der die Stolperfallen
 * fehlen koennen — genau die Faelle, vor denen sie warnen, haben in der
 * Vergangenheit Geld gekostet.
 *
 * Format der Datei:
 *
 *     ## begehung.versand
 *
 *     - **Kurzfassung fett.** Erklaerung dahinter.
 *     - Zweite Falle, auch ueber
 *       mehrere Zeilen.
 *
 * Alles vor der ersten `##`-Ueberschrift ist Vorspann und wird verworfen.
 */

const IST_UEBERSCHRIFT = /^##\s+(\S.*?)\s*$/;
const IST_AUFZAEHLUNG = /^[-*]\s+(.*)$/;

/**
 * Markdown → Map `kapitel-id → Stolperfallen`.
 *
 * Rein und ohne Seiteneffekt, damit sie ohne Dateisystem pruefbar ist.
 *
 * Drei Entscheidungen, die alle drei Absicht sind:
 *
 *  - **Fortsetzungszeilen gehoeren zum Aufzaehlungspunkt.** Eine eingerueckte
 *    oder direkt folgende Zeile ohne eigenen Strich ist der Umbruch eines
 *    langen Satzes, kein neuer Punkt. Wer sie als eigenen Eintrag zaehlte,
 *    zerrisse den Satz mitten im Warnhinweis.
 *  - **Doppelte Ueberschriften werden zusammengelegt**, nicht ueberschrieben.
 *    Sonst verschwindet beim Nachtragen eines zweiten Abschnitts fuer dasselbe
 *    Kapitel die aeltere Falle, ohne dass etwas rot wird.
 *  - **Leere Abschnitte kommen nicht in die Map.** Eine Ueberschrift ohne
 *    Punkte ist ein angefangener Abschnitt; die Kapitelseite soll dann gar
 *    keinen roten Kasten zeigen statt einen leeren.
 */
export const parseStolperfallen = (md: string): Map<TutorialKapitelId, string[]> => {
    const raus = new Map<TutorialKapitelId, string[]>();
    if (typeof md !== 'string' || !md) return raus;

    let kapitel: TutorialKapitelId | null = null;
    let punkte: string[] = [];
    let offen = false; // laeuft gerade ein Aufzaehlungspunkt, der fortgesetzt werden darf?

    const abschnittSchliessen = () => {
        if (kapitel && punkte.length) {
            const vorhanden = raus.get(kapitel);
            if (vorhanden) vorhanden.push(...punkte);
            else raus.set(kapitel, [...punkte]);
        }
        punkte = [];
        offen = false;
    };

    for (const zeile of md.split(/\r?\n/)) {
        const ueberschrift = IST_UEBERSCHRIFT.exec(zeile);
        if (ueberschrift) {
            abschnittSchliessen();
            kapitel = ueberschrift[1].trim();
            continue;
        }
        // Jede andere Ueberschriftstufe beendet den Abschnitt ebenfalls; sonst
        // rutschten die Punkte eines fremden Kapitels in den letzten `##`.
        if (/^#{1,6}\s/.test(zeile)) {
            abschnittSchliessen();
            kapitel = null;
            continue;
        }
        if (!kapitel) continue;

        const punkt = IST_AUFZAEHLUNG.exec(zeile.trim());
        if (punkt) {
            const text = punkt[1].trim();
            if (text) {
                punkte.push(text);
                offen = true;
            }
            continue;
        }
        if (!zeile.trim()) {
            offen = false;
            continue;
        }
        if (offen && punkte.length) {
            punkte[punkte.length - 1] = `${punkte[punkte.length - 1]} ${zeile.trim()}`;
        }
    }
    abschnittSchliessen();
    return raus;
};

/**
 * Die Stolperfallen des Bundles. Einmal geparst, danach nur gelesen — die
 * Datei aendert sich zur Laufzeit nicht.
 */
export const STOLPERFALLEN: ReadonlyMap<TutorialKapitelId, string[]> = parseStolperfallen(roheStolperfallen);

/** Stolperfallen eines Kapitels; leere Liste, wenn keine kuratiert sind. */
export const stolperfallenFuer = (id: TutorialKapitelId): string[] => STOLPERFALLEN.get(id) ?? [];
